import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CareMapState } from '../../caremap/CareMapState';
import { useSubscription } from '@meraki-internal/state';
import { NoteForm } from '../generic-notes/NoteForm';
import { CaremapNotesModelFactory } from '../model/CaremapNotesModelFactory';
import { ICaremapNoteResponse } from '../model/NotesClient';
import { EditNoteFormViewModel } from './EditNoteFormViewModel';
import { AlertPresenter } from '../../AlertBinder';
import { useEffect } from 'react';
import { CareMapNoteTracker } from '../model/CareMapNoteTracker';
import { NoteConflictResolver } from './NoteConflictResolver';
import { useAsyncEffect } from '../../support/useAsyncEffect';

interface IActiveCaremapNoteEditorPropsV2 {
    noteVM: EditNoteFormViewModel;
    onDeleted: () => void;
    hasPrivateCheckbox?: boolean;
    excludeMenu?: boolean;
    includeTemplates?: boolean;
    trackingFrom?: string;
}

export const ActiveCaremapNoteEditor: React.FC<IActiveCaremapNoteEditorPropsV2> = ({
    noteVM,
    onDeleted,
    excludeMenu,
    hasPrivateCheckbox = true,
    includeTemplates = false,
    trackingFrom
}) => {
    const tracker = useInstance(CareMapNoteTracker);
    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const notesFactory = useInstance(CaremapNotesModelFactory);
    useSubscription(() => notesFactory);

    // re-render when the VM merges a remote change or a conflict is resolved
    useSubscription(() => noteVM);

    const alert = useInstance(AlertPresenter);
    const conflictResolver = useInstance(NoteConflictResolver);

    const note = noteVM.getNote();

    useEffect(() => {
        tracker.onNoteViewed(note, trackingFrom);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // when a co-owner's change conflicts with a field the user has edited, walk the conflicts one
    // at a time (preview + Keep-mine / Use-theirs); Save stays blocked until all are resolved.
    useAsyncEffect(async () => {
        if (noteVM.getPendingConflicts().length > 0) {
            await conflictResolver.resolveAll(noteVM);
        }
    }, [noteVM.getPendingConflicts().length]);

    // dispose unsubscribes the VM from the event bus when the editor closes (the cleanup captures
    // the previous noteVM, so a VM swap is covered too)
    useEffect(() => {
        return () => {
            noteVM.dispose();
        };
    }, [noteVM]);

    // a collaborator deleted this note out from under us: tell the user and close the editor rather
    // than letting them keep editing (and failing to save) a note that no longer exists.
    useEffect(() => {
        if (noteVM.wasDeletedRemotely()) {
            alert.showAlert({
                header: 'Note deleted',
                message: 'This note was deleted by someone else, so it can no longer be edited.'
            });
            onDeleted();
        }
    }, [noteVM.wasDeletedRemotely()]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <NoteForm
            note={note}
            isAdd={false}
            canEdit={noteVM.canEdit()}
            noteTarget={note.otherAttributes.noteTarget}
            hasTitle
            excludeMenu={excludeMenu}
            includeTemplates={includeTemplates}
            hasPrivateCheckbox={hasPrivateCheckbox}
            canDelete={noteVM.canDelete()}
            addAttachmentRequiresInnerhivePlus={noteVM.addAttachmentRequiresInnerhivePlus}
            noteChanged={updatedNote => {
                noteVM.setNote(updatedNote as ICaremapNoteResponse);
            }}
            noteDeleted={async () => {
                try {
                    await noteVM.delete(trackingFrom);
                    onDeleted();
                }
                catch (err){
                    alert.showAndLogError(err);
                }
            }}
        />
    );
};
